import { io, Socket } from 'socket.io-client';
import deviceStore from '@/store/device.store';
import { AllStatus, DisplayModeSettings, RecordingDurationData } from '@/types/deviceControl';

// WebSocket 消息类型定义
export type ControlMessageType =
  | 'start-recording'
  | 'stop-recording'
  | 'pause-recording'
  | 'resume-recording'
  | 'get-all-status'
  | 'clear-subtitle'
  | 'heartbeat';

export type StatusMessageType =
  | 'recording-started'
  | 'recording-stopped'
  | 'recording-paused'
  | 'recording-resumed'
  | 'recording-duration-update'
  | 'ipc-disconnected';

interface WSMessage<T = any> {
  type: string;
  data?: T;
  requestId?: string;
  timestamp: number;
  source: 'control' | 'webpage';
}

interface WebSocketCallbacks {
  onConnected?: () => void;
  onDisconnected?: () => void;
  onErrorDisconnect?: () => void;
  onRecordingStarted?: () => void;
  onRecordingStopped?: () => void;
  onRecordingPaused?: () => void;
  onRecordingResumed?: () => void;
  onDurationUpdate?: (duration: number) => void;
}

// 启动录制状态码
export type StartRecordingStatus =
  | 'recording_started'    // 录制已启动
  | 'in_summary_page'      // 在总结页
  | 'crashed'              // 崩溃状态
  | 'no_rights'            // 无权益 (免费版)
  | 'no_rights_pay'        // 无权益 (需付费)
  | 'unknown_error';       // 未知错误

// 启动录制响应结构
export interface StartRecordingResponse {
  success: boolean;
  status: StartRecordingStatus;
  streamName?: string;
  errorCode?: string | number;
  message: string;
}

// WebSocket 响应消息（嵌套结构）
interface WSResponseMessage {
  requestId?: string;
  success: boolean;
  data?: {
    success?: boolean;
    result?: {
      success?: boolean;
      data?: {
        status?: StartRecordingStatus;
        streamName?: string;
        errorCode?: string | number;
      };
      message?: string;
    };
  };
  timestamp?: number;
}

const logsIgnoreEvents = ['recording-duration-update', 'heartbeat-response', 'subtitle-paragraph-original-updated', 'subtitle-paragraph-translated-updated', 'subtitle-unstable-updated'];

/**
 * 同传控制 WebSocket 处理器（静态类）
 * 负责管理与控制子进程的 WebSocket 连接，发送控制指令并接收状态更新
 */
class ControlWebSocketHandler {
  private static socket: Socket | null = null;
  private static heartbeatTimer: NodeJS.Timeout | undefined;
  private static callbacks: WebSocketCallbacks = {};
  private static heartbeatFailCount: number = 0; // 心跳失败计数
  private static readonly MAX_HEARTBEAT_FAIL: number = 5; // 最大心跳失败次数
  private static currentWsUrl: string = ''; // 当前连接的 WebSocket URL
  private static isErrorDisconnect: boolean = false; // 是否是异常断开连接
  // 存储待响应的请求 (requestId -> Promise resolver)
  private static pendingRequests: Map<string, {
    resolve: (value: any) => void;
    reject: (reason?: any) => void;
    timeout: NodeJS.Timeout;
  }> = new Map();

  constructor() {
    throw new Error('ControlWebSocketHandler is a static class');
  }

  /**
   * 注册回调函数
   */
  public static setCallbacks(callbacks: WebSocketCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * 清除所有回调
   */
  public static clearCallbacks(): void {
    this.callbacks = {};
  }

  /**
   * 生成请求 ID
   */
  private static generateRequestId(): string {
    return `ws_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * 发送 WebSocket 消息（不等待响应，fire-and-forget）
   */
  private static sendMessage(type: ControlMessageType, data?: any): boolean {
    if (!this.socket?.connected) {
      console.warn('[ControlWS] WebSocket 未连接，无法发送消息');
      return false;
    }

    const wsMessage: WSMessage = {
      type,
      data,
      requestId: this.generateRequestId(),
      timestamp: Date.now(),
      source: 'webpage',
    };

    console.log('[ControlWS 发送]', type, wsMessage);
    this.socket.emit(type, wsMessage);
    return true;
  }

  /**
   * 发送 WebSocket 消息并等待响应（用于需要响应结果的操作）
   */
  private static sendMessageWithResponse<T = any>(
    type: ControlMessageType,
    data?: any,
    timeout: number = 15000
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      if (!this.socket?.connected) {
        reject(new Error('WebSocket 未连接'));
        return;
      }

      const requestId = this.generateRequestId();
      const wsMessage: WSMessage = {
        type,
        data,
        requestId,
        timestamp: Date.now(),
        source: 'webpage',
      };

      // 设置超时处理
      const timeoutHandle = setTimeout(() => {
        this.pendingRequests.delete(requestId);
        reject(new Error(`请求超时：${type}`));
      }, timeout);

      // 存储请求
      this.pendingRequests.set(requestId, { resolve, reject, timeout: timeoutHandle });

      console.log('[ControlWS 发送 - 等待响应]', type, wsMessage);
      this.socket.emit(type, wsMessage);
    });
  }

  /**
   * 处理响应消息
   */
  private static handleResponse(requestId: string, response: any): void {
    const pending = this.pendingRequests.get(requestId);
    if (pending) {
      clearTimeout(pending.timeout);
      this.pendingRequests.delete(requestId);
      pending.resolve(response);
    }
  }

  /**
   * 解析启动录制响应
   */
  private static parseStartRecordingResponse(response: WSResponseMessage): StartRecordingResponse {
    // 提取嵌套的响应数据
    const resultSuccess = response?.data?.result?.success ?? false;
    const resultData = response?.data?.result?.data;
    const resultMessage = response?.data?.result?.message || '';

    const status: StartRecordingStatus = resultData?.status || 'unknown_error';
    const streamName = resultData?.streamName;
    const errorCode = resultData?.errorCode;

    return {
      success: resultSuccess,
      status,
      streamName,
      errorCode,
      message: resultMessage,
    };
  }

  /**
   * 设置状态监听器
   */
  private static setupStatusListeners(socket: Socket): void {
    // ========== 请求 - 响应监听器（所有控制指令响应） ==========

    socket.onAny((event: string, message: any) => {
      if (logsIgnoreEvents.includes(event)) return;
      console.log(`[ControlWS 接收] ${event} ->`, message);
    });

    // 启动录制响应
    socket.on('start-recording-response', (message: WSResponseMessage) => {
      if (message.requestId) {
        this.handleResponse(message.requestId, message);
      }
    });

    // 停止录制响应
    socket.on('stop-recording-response', (message: any) => {
      if (message.requestId) {
        this.handleResponse(message.requestId, message);
      }
    });

    // 暂停录制响应
    socket.on('pause-recording-response', (message: any) => {
      if (message.requestId) {
        this.handleResponse(message.requestId, message);
      }
    });

    // 恢复录制响应
    socket.on('resume-recording-response', (message: any) => {
      if (message.requestId) {
        this.handleResponse(message.requestId, message);
      }
    });

    // 心跳响应
    socket.on('heartbeat-response', (message: any) => {
      // 收到心跳响应，清空失败计数
      this.heartbeatFailCount = 0;

      if (message.requestId) {
        this.handleResponse(message.requestId, message);
      }
    });

    // ========== 状态广播监听器（服务端主动推送） ==========
    // 录制已开始（状态广播）
    socket.on('recording-started', (message: WSMessage) => {
      deviceStore.getState().updateRecordingStatus({
        isRecording: true,
        streamName: message.data?.streamName,
        startTime: Date.now(),
      });
      this.callbacks.onRecordingStarted?.();
    });

    // 录制已停止
    socket.on('recording-stopped', () => {
      deviceStore.getState().updateRecordingStatus({
        isRecording: false,
      });
      this.callbacks.onRecordingStopped?.();
    });

    // 录制已暂停
    socket.on('recording-paused', () => {
      deviceStore.getState().updateRecordingStatus({
        isPaused: true,
        isRecording: true,
      });
      this.callbacks.onRecordingPaused?.();
    });

    // 录制已恢复
    socket.on('recording-resumed', () => {
      deviceStore.getState().updateRecordingStatus({
        isPaused: false,
        isRecording: true,
      });
      this.callbacks.onRecordingResumed?.();
    });

    // 录制时长更新
    socket.on('recording-duration-update', (message: RecordingDurationData) => {
      if (message?.duration !== undefined) {
        deviceStore.getState().updateRecordingStatus({
          duration: message.duration,
        });
        this.callbacks.onDurationUpdate?.(message.duration);
      }
    });

    // 显示模式更新
    socket.on('display-mode-changed', (message: DisplayModeSettings) => {
      deviceStore.getState().setDisplayMode({
        mode: message.mode
      });
    });

    // 所有状态更新
    socket.on('all-status-response', (message: AllStatus) => {
      deviceStore.getState().updateAllStatus(message);
    });
  }

  /**
   * 连接 WebSocket
   */
  public static connect(wsUrl: string): void {
    // 如果已经连接，先断开
    if (this.socket?.connected) {
      console.log('[ControlWS] 已存在连接，先断开');
      this.disconnect(false);
    }

    console.log('[ControlWS 连接] 正在连接到：', wsUrl);

    // 保存当前连接的 URL，用于重连
    this.currentWsUrl = wsUrl;

    try {
      const socket = io(wsUrl, {
        transports: ['websocket'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
      });

      this.socket = socket;

      // 连接成功
      socket.on('connect', () => {
        console.log('[ControlWS 连接] 连接成功');
        deviceStore.getState().setWSConnected(true);
        this.heartbeatFailCount = 0; // 重置心跳失败计数
        this.startHeartbeat();
        this.callbacks.onConnected?.();

        // 连接成功后获取当前状态
        this.getAllStatus();
      });

      // 连接断开
      socket.on('disconnect', (reason: string) => {
        console.log('[ControlWS 断开]', reason);
        console.log('deviceStore.getState().wsConnected = ', deviceStore.getState().wsConnected);
        this.stopHeartbeat();
        // 如果连接异常断开，则触发异常断开回调
        if (this.isErrorDisconnect || deviceStore.getState().wsConnected) {
          this.callbacks.onErrorDisconnect?.();
          this.isErrorDisconnect = false;
        }

        deviceStore.getState().setWSConnected(false);
        this.callbacks.onDisconnected?.();
      });

      // 连接错误
      socket.on('connect_error', (error: Error) => {
        console.error('[ControlWS 错误]', error);
        deviceStore.getState().setWSConnected(false);
      });

      // 监听状态消息
      this.setupStatusListeners(socket);
    } catch (error) {
      console.error('[ControlWS 连接] 连接失败：', error);
    }
  }

  /**
   * 断开连接
   */
  public static disconnect(isErrorDisconnect: boolean): void {
    this.stopHeartbeat();

    // 清理所有待处理的请求
    this.pendingRequests.forEach((pending) => {
      clearTimeout(pending.timeout);
      pending.reject(new Error('WebSocket 连接已断开'));
    });
    this.pendingRequests.clear();

    // 重置心跳失败计数
    this.heartbeatFailCount = 0;

    deviceStore.getState().setWSConnected(false);

    if (this.socket) {
      this.isErrorDisconnect = isErrorDisconnect;
      console.log('[ControlWS 断开] 主动断开连接，isErrorDisconnect = ', isErrorDisconnect);
      this.socket.disconnect();
      this.socket = null;
    }
  }

  /**
   * 启动心跳
   */
  private static startHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
    }

    this.heartbeatTimer = setInterval(() => {
      // 发送心跳，计数 +1
      this.heartbeatFailCount++;

      // 检查是否超过最大失败次数
      if (this.heartbeatFailCount >= this.MAX_HEARTBEAT_FAIL) {
        console.error('[ControlWS] 心跳连续失败，触发重连');
        this.handleHeartbeatFailure();
        return;
      }

      // 发送心跳消息
      this.sendMessage('heartbeat');
    }, 3000); // 每 3 秒发送一次心跳
  }

  /**
   * 处理心跳失败，触发重连
   */
  private static handleHeartbeatFailure(): void {
    console.log('[ControlWS] 开始重连流程');

    // 先断开当前连接
    this.disconnect(true);

    // 延迟 2 秒后重连
    setTimeout(() => {
      if (this.currentWsUrl) {
        console.log('[ControlWS] 尝试重新连接：', this.currentWsUrl);
        this.connect(this.currentWsUrl);
      }
    }, 2000);
  }

  /**
   * 停止心跳
   */
  private static stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = undefined;
    }
  }

  /**
   * 开始录制
   * @param streamName 流名称
   * @returns Promise<StartRecordingResponse> 包含详细的启动结果
   */
  public static async startRecording(streamName?: string): Promise<StartRecordingResponse> {
    try {
      const response = await this.sendMessageWithResponse<WSResponseMessage>(
        'start-recording',
        { stream_name: streamName }
      );

      return this.parseStartRecordingResponse(response);
    } catch (error) {
      console.error('[ControlWS] 启动录制失败：', error);
      return {
        success: false,
        status: 'unknown_error',
        message: error instanceof Error ? error.message : '启动录制失败',
      };
    }
  }

  /**
   * 停止录制
   */
  public static async stopRecording(): Promise<any> {
    try {
      return await this.sendMessageWithResponse('stop-recording');
    } catch (error) {
      console.error('[ControlWS] 停止录制失败：', error);
      throw error;
    }
  }

  /**
   * 暂停录制
   */
  public static async pauseRecording(): Promise<any> {
    try {
      return await this.sendMessageWithResponse('pause-recording');
    } catch (error) {
      console.error('[ControlWS] 暂停录制失败：', error);
      throw error;
    }
  }

  /**
   * 恢复录制
   */
  public static async resumeRecording(): Promise<any> {
    try {
      return await this.sendMessageWithResponse('resume-recording');
    } catch (error) {
      console.error('[ControlWS] 恢复录制失败：', error);
      throw error;
    }
  }

  /**
   * 获取所有状态（不等待响应，状态通过广播消息返回）
   */
  public static getAllStatus(): boolean {
    return this.sendMessage('get-all-status');
  }

  /**
   * 清除字幕（不等待响应）
   */
  public static clearSubtitle(): boolean {
    return this.sendMessage('clear-subtitle');
  }

  /**
   * 检查是否已连接
   */
  public static isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * 重置所有状态（用于清理）
   */
  public static reset(): void {
    this.disconnect(false);
    this.clearCallbacks();
  }
}

export default ControlWebSocketHandler;

